// ═══════════════════════════════════════════════════════════════
// GiftEntryForm.jsx
// Full gift entry form for all 4 gift types.
// Used inside QuickGiftModal (new gift) and GiftsSection (edit).
// Calls giftsService directly — no AppContext gift state.
// After save, calls onSaved() so parent can refresh member.
// Path: src/components/features/GiftEntryForm.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from "react";
import { Save, X, AlertCircle, Film, Package, ChevronDown, ChevronUp } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, CHAPTER_ID, ICON_SIZES } from "../../constants/appConfig";
import {
  GIFT_TYPES,
  PAYMENT_METHODS,
  formatCurrency,
} from "../../constants/giftTypes";
import { giftsService } from "../../services/giftsService";
import {
  showsService,
  packagesService,
  sponsorshipsService,
} from "../../services/showSponsorshipsService";
import { supabase } from "../../lib/supabase";
import { campaignService } from "../../services/campaignService";
import { scholarshipsService } from "../../services/scholarshipService";
import { useApp } from "../../context/AppContext";
import { contactLogService } from "../../services/contactLogService";

// ── Today's date in YYYY-MM-DD ────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);

// ── Empty form state by gift type ─────────────────────────────
const emptyForm = (giftType = "cash", firstFundId = null) => ({
  giftType,
  giftDate: today(),
  fundId: firstFundId,
  // Cash fields
  amount: "",
  paymentMethod: "check",
  checkNumber: "",
  fairMarketValue: "",
  goodsServicesDescription: "",
  // In-kind
  inkindDescription: "",
  // Stock
  stockTicker: "",
  stockShares: "",
  stockTransferDate: today(),
  // DAF
  dafSponsoringOrg: "",
  dafGrantId: "",
  // Shared
  notes: "",
  campaignId: null,
  scholarshipId: null,
});

// ── Shared label style ────────────────────────────────────────
const Label = ({ children, palette, required }) => (
  <span
    style={{
      fontSize: FONT_SIZES.xs,
      fontWeight: 600,
      color: palette.text2,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      display: "block",
      marginBottom: 4,
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </span>
);

// ── Shared input style ────────────────────────────────────────
const inputStyle = (palette) => ({
  width: "100%",
  padding: "8px 12px",
  borderRadius: 8,
  fontSize: FONT_SIZES.sm,
  fontFamily: FONT_FAMILY,
  border: `1px solid ${palette.inputBorder}`,
  background: palette.inputBg,
  color: palette.inputColor,
  outline: "none",
  boxSizing: "border-box",
});

const selectStyle = (palette) => ({
  ...inputStyle(palette),
  cursor: "pointer",
});

export const GiftEntryForm = ({
  member,
  gift = null, // null = new gift, object = edit mode
  onSaved, // callback after successful save
  onCancel,
  palette,
  activeFunds,
  showTypeSelector = true,
}) => {
  const { currentUser, showMessage, bumpDataVersion, updateMember } = useApp();
  const isEdit = !!gift;

  const [form, setForm] = useState(() =>
    gift
      ? {
          giftType: gift.giftType,
          giftDate: gift.giftDate,
          fundId: gift.fundId || null,
          amount: gift.amount != null ? String(gift.amount) : "",
          paymentMethod: gift.paymentMethod || "check",
          checkNumber: gift.checkNumber || "",
          fairMarketValue:
            gift.fairMarketValue != null ? String(gift.fairMarketValue) : "",
          goodsServicesDescription: gift.goodsServicesDescription || "",
          inkindDescription: gift.inkindDescription || "",
          stockTicker: gift.stockTicker || "",
          stockShares: gift.stockShares != null ? String(gift.stockShares) : "",
          stockTransferDate: gift.stockTransferDate || today(),
          dafSponsoringOrg: gift.dafSponsoringOrg || "",
          dafGrantId: gift.dafGrantId || "",
          notes: gift.notes || "",
          campaignId: gift.campaignId || null,
          scholarshipId: gift.scholarshipId || null,
        }
      : emptyForm("cash", activeFunds?.[0]?.id || null)
  );

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // ── Campaign dropdown state ───────────────────────────────
  const [campaignOptions, setCampaignOptions] = useState([]);
  useEffect(() => {
    campaignService.getForDropdown().then(setCampaignOptions).catch(() => {});
  }, []);

  // ── Scholarship dropdown state ──────────────────────────────
  const [scholarshipOptions, setScholarshipOptions] = useState([]);
  useEffect(() => {
    scholarshipsService.getForDropdown().then(setScholarshipOptions).catch(() => {});
  }, []);

  // ── Show Sponsorship panel state ──────────────────────────
  const [sponsorshipOpen, setSponsorshipOpen] = useState(false);
  const [activeShows, setActiveShows] = useState([]);
  const [qualifyingPackages, setQualifyingPackages] = useState([]);
  const [selectedShowId, setSelectedShowId] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [creditAsPrinted, setCreditAsPrinted] = useState(
    member?.credentialedFullName ||
      `${member?.firstName || ""} ${member?.lastName || ""}`.trim() ||
      ""
  );
  const [sponsorshipNotes, setSponsorshipNotes] = useState("");
  const debounceRef = useRef(null);

  // Load active shows on mount (for sponsorship dropdown)
  useEffect(() => {
    if (isEdit) return; // no sponsorship panel on edits
    showsService.getActiveShows().then(setActiveShows).catch(() => {});
  }, [isEdit]);

  // Auto-suggest packages when amount or show changes (debounced 400ms)
  useEffect(() => {
    if (isEdit) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const amount = parseFloat(form.amount) || 0;
    if (amount <= 0) {
      setQualifyingPackages([]);
      setSponsorshipOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const pkgs = await packagesService.getPackagesForAmount(
          amount,
          selectedShowId || undefined
        );
        setQualifyingPackages(pkgs);

        if (pkgs.length > 0 && !sponsorshipOpen) {
          setSponsorshipOpen(true);
          // Auto-select highest qualifying tier (first result)
          if (!selectedPackageId) {
            setSelectedPackageId(pkgs[0].id);
          }
        }
      } catch (error) {
        // silent — non-critical
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [form.amount, selectedShowId, isEdit]);

  // Keep fundId in sync when activeFunds loads
  useEffect(() => {
    if (!form.fundId && activeFunds?.length > 0) {
      setForm((prev) => ({ ...prev, fundId: activeFunds[0].id }));
    }
  }, [activeFunds]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // ── Derived: deductible amount preview ────────────────────────
  const amountNum = parseFloat(form.amount) || 0;
  const fmvNum = parseFloat(form.fairMarketValue) || 0;
  const deductible =
    form.giftType === "cash" && amountNum > 0 ? amountNum - fmvNum : null;

  // ── Validation ────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.giftDate) e.giftDate = "Required";

    if (form.giftType === "cash") {
      if (!form.amount || parseFloat(form.amount) <= 0)
        e.amount = "Amount required";
      if (!form.paymentMethod) e.paymentMethod = "Required";
      if (fmvNum > 0 && !form.goodsServicesDescription)
        e.goodsServicesDescription =
          "Description required when G&S value is entered";
      if (fmvNum >= amountNum && fmvNum > 0)
        e.fairMarketValue = "G&S value must be less than gift amount";
    }
    if (form.giftType === "inkind") {
      if (!form.inkindDescription.trim())
        e.inkindDescription = "Description required";
    }
    if (form.giftType === "stock") {
      if (!form.stockTicker.trim()) e.stockTicker = "Ticker required";
      if (!form.stockShares || parseFloat(form.stockShares) <= 0)
        e.stockShares = "Shares required";
    }
    if (form.giftType === "daf") {
      if (!form.dafSponsoringOrg.trim())
        e.dafSponsoringOrg = "Sponsoring organization required";
      if (!form.amount || parseFloat(form.amount) <= 0)
        e.amount = "Grant amount required";
    }
    return e;
  };

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        memberId: member?.id || null,
        giftType: form.giftType,
        giftDate: form.giftDate,
        fundId: form.fundId || null,
        campaignId: form.campaignId || null,
        scholarshipId: form.scholarshipId || null,
        notes: form.notes || null,
        // Cash
        amount:
          form.giftType === "cash" || form.giftType === "daf"
            ? parseFloat(form.amount)
            : null,
        paymentMethod: form.giftType === "cash" ? form.paymentMethod : null,
        checkNumber:
          form.giftType === "cash" && form.paymentMethod === "check"
            ? form.checkNumber || null
            : null,
        fairMarketValue: form.giftType === "cash" && fmvNum > 0 ? fmvNum : null,
        goodsServicesDescription:
          form.giftType === "cash" && fmvNum > 0
            ? form.goodsServicesDescription
            : null,
        // In-kind
        inkindDescription:
          form.giftType === "inkind" ? form.inkindDescription : null,
        // Stock
        stockTicker:
          form.giftType === "stock" ? form.stockTicker.toUpperCase() : null,
        stockShares:
          form.giftType === "stock" ? parseFloat(form.stockShares) : null,
        stockTransferDate:
          form.giftType === "stock" ? form.stockTransferDate : null,
        // DAF
        dafSponsoringOrg:
          form.giftType === "daf" ? form.dafSponsoringOrg : null,
        dafGrantId:
          form.giftType === "daf" && form.dafGrantId ? form.dafGrantId : null,
      };

      if (isEdit) {
        await giftsService.update(gift.id, payload);
        showMessage("Gift updated");
      } else {
        // Use giftsService for normal gifts, direct insert when sponsorship needed
        const wantSponsorship =
          sponsorshipOpen && selectedPackageId && member;

        if (wantSponsorship) {
          // Direct insert so we reliably get the gift ID back
          const { data: rows, error: insertErr } = await supabase
            .from("gifts")
            .insert({
              member_id: member.id,
              gift_type: payload.giftType,
              gift_date: payload.giftDate,
              fund_id: payload.fundId || null,
              amount: payload.amount,
              payment_method: payload.paymentMethod,
              check_number: payload.checkNumber,
              fair_market_value: payload.fairMarketValue,
              goods_services_description: payload.goodsServicesDescription,
              inkind_description: payload.inkindDescription,
              stock_ticker: payload.stockTicker,
              stock_shares: payload.stockShares,
              stock_transfer_date: payload.stockTransferDate,
              daf_sponsoring_org: payload.dafSponsoringOrg,
              daf_grant_id: payload.dafGrantId,
              notes: payload.notes,
              created_by: currentUser?.id || null,
            })
            .select("id");

          if (insertErr) throw insertErr;

          const giftId = rows && rows.length > 0 ? rows[0].id : null;

          if (giftId) {
            try {
              await sponsorshipsService.createSponsorship(
                {
                  showId: selectedShowId || null,
                  packageId: selectedPackageId,
                  giftId: giftId,
                  memberId: member.id,
                  creditAsPrinted:
                    creditAsPrinted.trim() || member?.firstName || "Sponsor",
                  notes: null,
                },
                CHAPTER_ID,
                currentUser?.id
              );
              showMessage("Gift recorded with sponsorship");
            } catch (sponsorshipError) {
              showMessage(
                "Gift recorded, but sponsorship failed — " +
                  sponsorshipError.message,
                "error"
              );
            }
          } else {
            showMessage("Gift saved but could not link sponsorship", "warning");
          }
        } else {
          // Standard gift creation (works with member=null from GiftsView)
          await giftsService.create(payload, currentUser?.id);
          showMessage("Gift recorded");
        }
      }
      // Auto-advance Welcome Journey stage if gift is linked to a campaign
      if (!isEdit && payload.campaignId && (member?.id || payload.memberId)) {
        try {
          await campaignService.advanceJourneyOnGift(
            member?.id || payload.memberId,
            payload.campaignId
          );
        } catch (journeyErr) {
          console.error("Journey stage advance failed", journeyErr);
        }
      }

      // Auto-advance portfolio move to "acknowledge" on new gift
      if (!isEdit && member?.id && member?.assignedDirector) {
        const currentMove = member.portfolioMove || "cultivate";
        if (currentMove !== "acknowledge") {
          try {
            await updateMember(
              member.id,
              {
                portfolioMove: "acknowledge",
                portfolioMoveDate: new Date().toISOString(),
              },
              true
            );
            await contactLogService.create(member.id, {
              type: "portfolio_move",
              subject: `${currentMove.charAt(0).toUpperCase() + currentMove.slice(1)} → Acknowledge`,
              notes: `Portfolio move: auto-advanced to Acknowledge (gift recorded)`,
              date: new Date().toISOString(),
            });
          } catch (moveErr) {
            console.error("Auto-advance portfolio move failed", moveErr);
          }
        }
      }

      onSaved?.();
      bumpDataVersion();
    } catch (error) {
      showMessage("Error saving gift — " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Error helper ──────────────────────────────────────────────
  const FieldError = ({ field }) =>
    errors[field] ? (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginTop: 4,
          fontSize: 11,
          color: "#ef4444",
        }}
      >
        <AlertCircle size={ICON_SIZES.xs} />
        {errors[field]}
      </div>
    ) : null;

  const type = GIFT_TYPES.find((t) => t.id === form.giftType);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Gift Type Selector ─────────────────────────────────── */}
      {showTypeSelector && (
        <div>
          <Label palette={palette}>Gift Type</Label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {GIFT_TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() =>
                  setForm((prev) => ({
                    ...emptyForm(t.id, prev.fundId),
                    giftDate: prev.giftDate,
                    fundId: prev.fundId,
                    notes: prev.notes,
                  }))
                }
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: `1px solid ${
                    form.giftType === t.id
                      ? palette.accent1
                      : palette.inputBorder
                  }`,
                  background:
                    form.giftType === t.id
                      ? `${palette.accent1}15`
                      : "transparent",
                  color:
                    form.giftType === t.id ? "#fff" : palette.text2,
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {t.shortLabel}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Gift Date + Fund (all types) ──────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        <div>
          <Label palette={palette} required>
            Gift Date
          </Label>
          <input
            type="date"
            value={form.giftDate}
            onChange={(e) => set("giftDate", e.target.value)}
            style={inputStyle(palette)}
          />
          <FieldError field="giftDate" />
        </div>

        {type?.hasFund && (
          <div>
            <Label palette={palette}>Fund</Label>
            <select
              value={form.fundId || ""}
              onChange={(e) => set("fundId", e.target.value || null)}
              style={selectStyle(palette)}
            >
              <option value="">(No fund)</option>
              {(activeFunds || []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Campaign Response (optional) ──────────────────────── */}
      {campaignOptions.length > 0 && (
        <div>
          <Label palette={palette}>Campaign Response (optional)</Label>
          <select
            value={form.campaignId || ""}
            onChange={(e) => set("campaignId", e.target.value || null)}
            style={selectStyle(palette)}
          >
            <option value="">— None —</option>
            {campaignOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.date_sent ? ` (${new Date(c.date_sent + "T12:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" })})` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Named Scholarship (conditional) ─────────────────────── */}
      {scholarshipOptions.length > 0 && (() => {
        const selectedFund = (activeFunds || []).find((f) => f.id === form.fundId);
        const isScholarshipFund = selectedFund && selectedFund.name.toLowerCase().includes("scholarship");
        return isScholarshipFund ? (
          <div>
            <Label palette={palette}>Named Scholarship</Label>
            <select
              value={form.scholarshipId || ""}
              onChange={(e) => set("scholarshipId", e.target.value || null)}
              style={selectStyle(palette)}
            >
              <option value="">— None —</option>
              {scholarshipOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        ) : null;
      })()}

      {/* ── Cash / CC / Check fields ──────────────────────────── */}
      {form.giftType === "cash" && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <div>
              <Label palette={palette} required>
                Amount
              </Label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                style={inputStyle(palette)}
              />
              <FieldError field="amount" />
            </div>

            <div>
              <Label palette={palette} required>
                Payment Method
              </Label>
              <select
                value={form.paymentMethod}
                onChange={(e) => set("paymentMethod", e.target.value)}
                style={selectStyle(palette)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
              <FieldError field="paymentMethod" />
            </div>
          </div>

          {form.paymentMethod === "check" && (
            <div>
              <Label palette={palette}>Check Number</Label>
              <input
                type="text"
                placeholder="Check #"
                value={form.checkNumber}
                onChange={(e) => set("checkNumber", e.target.value)}
                style={inputStyle(palette)}
              />
            </div>
          )}

          {/* G&S / Quid Pro Quo */}
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: "#fffbeb",
              border: "1px solid #fde68a",
            }}
          >
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                color: "#92400e",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 10,
              }}
            >
              Goods &amp; Services (Quid Pro Quo) — optional
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 10,
              }}
            >
              <div>
                <Label palette={palette}>G&amp;S Fair Market Value</Label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.fairMarketValue}
                  onChange={(e) => set("fairMarketValue", e.target.value)}
                  style={inputStyle(palette)}
                />
                <FieldError field="fairMarketValue" />
              </div>
              <div>
                <Label palette={palette}>Net Deductible (computed)</Label>
                <div
                  style={{
                    ...inputStyle(palette),
                    background: `${palette.inputBg}80`,
                    color: deductible != null ? palette.text1 : palette.text3,
                    fontWeight: deductible != null ? 700 : 400,
                  }}
                >
                  {deductible != null ? formatCurrency(deductible) : "—"}
                </div>
              </div>
            </div>
            {fmvNum > 0 && (
              <div>
                <Label palette={palette} required>
                  G&amp;S Description (required when FMV &gt; 0)
                </Label>
                <input
                  type="text"
                  placeholder="e.g., Gala dinner table for 2"
                  value={form.goodsServicesDescription}
                  onChange={(e) =>
                    set("goodsServicesDescription", e.target.value)
                  }
                  style={inputStyle(palette)}
                />
                <FieldError field="goodsServicesDescription" />
              </div>
            )}
          </div>
        </>
      )}

      {/* ── In-Kind fields ────────────────────────────────────── */}
      {form.giftType === "inkind" && (
        <>
          <div>
            <Label palette={palette} required>
              Description of In-Kind Gift
            </Label>
            <textarea
              rows={3}
              placeholder="Describe the donated goods or services..."
              value={form.inkindDescription}
              onChange={(e) => set("inkindDescription", e.target.value)}
              style={{ ...inputStyle(palette), resize: "vertical" }}
            />
            <FieldError field="inkindDescription" />
          </div>
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              fontSize: 11,
              color: "#166534",
              lineHeight: 1.5,
            }}
          >
            <strong>IRS Note:</strong> No value is assigned to in-kind gifts on
            the receipt. The donor is responsible for determining fair market
            value for their tax deduction.
          </div>
        </>
      )}

      {/* ── Stock / Securities fields ─────────────────────────── */}
      {form.giftType === "stock" && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
            }}
          >
            <div>
              <Label palette={palette} required>
                Ticker Symbol
              </Label>
              <input
                type="text"
                placeholder="e.g., AAPL"
                value={form.stockTicker}
                onChange={(e) =>
                  set("stockTicker", e.target.value.toUpperCase())
                }
                style={inputStyle(palette)}
              />
              <FieldError field="stockTicker" />
            </div>
            <div>
              <Label palette={palette} required>
                Shares
              </Label>
              <input
                type="number"
                min="0.0001"
                step="0.0001"
                placeholder="0.0000"
                value={form.stockShares}
                onChange={(e) => set("stockShares", e.target.value)}
                style={inputStyle(palette)}
              />
              <FieldError field="stockShares" />
            </div>
            <div>
              <Label palette={palette} required>
                Transfer Date
              </Label>
              <input
                type="date"
                value={form.stockTransferDate}
                onChange={(e) => set("stockTransferDate", e.target.value)}
                style={inputStyle(palette)}
              />
            </div>
          </div>
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              fontSize: 11,
              color: "#166534",
              lineHeight: 1.5,
            }}
          >
            <strong>IRS Note:</strong> Deductible amount is the FMV of shares on
            the transfer date, determined by the donor or their financial
            advisor.
          </div>
        </>
      )}

      {/* ── DAF fields ────────────────────────────────────────── */}
      {form.giftType === "daf" && (
        <>
          <div>
            <Label palette={palette} required>
              Sponsoring Organization
            </Label>
            <input
              type="text"
              placeholder="e.g., Fidelity Charitable"
              value={form.dafSponsoringOrg}
              onChange={(e) => set("dafSponsoringOrg", e.target.value)}
              style={inputStyle(palette)}
            />
            <FieldError field="dafSponsoringOrg" />
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <Label palette={palette}>Grant / Reference ID</Label>
              <input
                type="text"
                placeholder="Optional"
                value={form.dafGrantId}
                onChange={(e) => set("dafGrantId", e.target.value)}
                style={inputStyle(palette)}
              />
            </div>
            <div>
              <Label palette={palette} required>
                Grant Amount
              </Label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                style={inputStyle(palette)}
              />
              <FieldError field="amount" />
            </div>
          </div>
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              fontSize: 11,
              color: "#1e40af",
              lineHeight: 1.5,
            }}
          >
            <strong>Note:</strong> The receipt for a DAF grant is an
            acknowledgment only — not a tax receipt. The donor's deduction was
            established when they contributed to the sponsoring organization.
          </div>
        </>
      )}

      {/* ── Show Sponsorship Panel ──────────────────────────── */}
      {!isEdit && qualifyingPackages.length > 0 && (
        <div
          style={{
            borderRadius: 8,
            border: `1px solid ${sponsorshipOpen ? palette.accent1 : palette.inputBorder}`,
            background: sponsorshipOpen ? `${palette.accent1}06` : "transparent",
            overflow: "hidden",
            transition: "all 0.2s",
          }}
        >
          {/* Header — click to toggle */}
          <div
            onClick={() => setSponsorshipOpen(!sponsorshipOpen)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              cursor: "pointer",
              background: sponsorshipOpen ? `${palette.accent1}10` : "transparent",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Film size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  color: palette.accent1,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontFamily: FONT_FAMILY,
                }}
              >
                Show Sponsorship
              </span>
              <span
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  color: "#16a34a",
                  background: "#dcfce7",
                  padding: "1px 6px",
                  borderRadius: 8,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {qualifyingPackages.length} qualifying
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {sponsorshipOpen ? (
                <X
                  size={ICON_SIZES.sm}
                  style={{ color: palette.text3 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSponsorshipOpen(false);
                    setSelectedShowId("");
                    setSelectedPackageId("");
                  }}
                />
              ) : (
                <ChevronDown size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
              )}
            </div>
          </div>

          {/* Panel content */}
          {sponsorshipOpen && (
            <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Row: Package + Show (side by side, compact) */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <Label palette={palette} required>Package</Label>
                  <select
                    value={selectedPackageId}
                    onChange={(e) => setSelectedPackageId(e.target.value)}
                    style={selectStyle(palette)}
                  >
                    <option value="">— Select —</option>
                    {qualifyingPackages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.name} ({formatCurrency(pkg.min_amount)}+)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label palette={palette}>Show (optional)</Label>
                  <select
                    value={selectedShowId}
                    onChange={(e) => setSelectedShowId(e.target.value)}
                    style={selectStyle(palette)}
                  >
                    <option value="">— General / Season —</option>
                    {activeShows.map((show) => (
                      <option key={show.id} value={show.id}>
                        {show.title} ({show.season})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Credit as printed */}
              <div>
                <Label palette={palette} required>Credit As Printed</Label>
                <input
                  type="text"
                  value={creditAsPrinted}
                  onChange={(e) => setCreditAsPrinted(e.target.value)}
                  placeholder="Name for program / recognition"
                  style={inputStyle(palette)}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Internal Notes (all types) ────────────────────────── */}
      <div>
        <Label palette={palette}>Internal Notes</Label>
        <textarea
          rows={2}
          placeholder="Staff notes — not printed on receipt..."
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          style={{ ...inputStyle(palette), resize: "vertical" }}
        />
      </div>

      {/* ── Actions ───────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          paddingTop: 4,
        }}
      >
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 8,
            border: `1px solid ${palette.inputBorder}`,
            background: "transparent",
            color: palette.text2,
            fontSize: FONT_SIZES.sm,
            fontWeight: 600,
            fontFamily: FONT_FAMILY,
            cursor: "pointer",
          }}
        >
          <X size={ICON_SIZES.sm} /> Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 20px",
            borderRadius: 8,
            border: "none",
            background: saving ? `${palette.accent1}70` : palette.accent1,
            color: "#ffffff",
            fontSize: FONT_SIZES.sm,
            fontWeight: 700,
            fontFamily: FONT_FAMILY,
            cursor: saving ? "default" : "pointer",
            transition: "all 0.15s",
          }}
        >
          <Save size={ICON_SIZES.sm} />
          {saving ? "Saving..." : isEdit ? "Update Gift" : "Record Gift"}
        </button>
      </div>
    </div>
  );
};
